import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Avatar, Button, Card, Result, Skeleton, Space, Tabs, Tag, Typography } from 'antd';
import { CalendarOutlined, DollarOutlined, MessageOutlined } from '@ant-design/icons';
import type { StudentDetail, StudentGuardianLink } from '@velora/contracts';
import { ApiError, productApi } from '../../../api';

/**
 * Admin surface — Screen 18. Student profile.
 * First real slice: header + guardians read from the DB. Attendance / Marks /
 * Fees / Conduct tabs still show a "waiting for module" placeholder — they read
 * derived data from tables that don't exist yet.
 *
 * URL segment `:code` is the student's admission number (D-08). We resolve it
 * against /api/product/institutions/:slug/students/:adm.
 */
export function StudentProfilePage() {
  const { institution = '', code = '' } = useParams<{ institution: string; code: string }>();
  const navigate = useNavigate();

  const q = useQuery({
    queryKey: ['student', institution, code],
    queryFn: () => productApi.getStudent(institution, code),
    enabled: !!institution && !!code,
    retry: false,
  });

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space size={4}>
        <Button type="text" size="small" onClick={() => navigate(`/i/${institution}/admin/students`)}>
          ← Students
        </Button>
        <Typography.Text type="secondary">/</Typography.Text>
        <Typography.Text style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>
          {code}
        </Typography.Text>
      </Space>

      {q.isLoading && <Skeleton active paragraph={{ rows: 6 }} />}
      {q.isError && <ErrorState err={q.error} />}
      {q.data && <ProfileBody detail={q.data} />}
    </Space>
  );
}

function ErrorState({ err }: { err: unknown }) {
  if (err instanceof ApiError && err.status === 404) {
    return (
      <Card>
        <Result
          status="404"
          title="Student not found"
          subTitle="This admission number is not on file for this institution."
        />
      </Card>
    );
  }
  return (
    <Alert
      type="error"
      showIcon
      message="Could not load student"
      description={err instanceof Error ? err.message : String(err)}
    />
  );
}

function ProfileBody({ detail }: { detail: StudentDetail }) {
  const { student, guardians } = detail;
  const primary = guardians.find((g) => g.is_primary) ?? guardians[0] ?? null;
  const secondaries = guardians.filter((g) => g !== primary);

  return (
    <>
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        <Avatar size={74} style={{ background: '#2f6ab0', fontSize: 24, flexShrink: 0 }}>
          {student.name
            .split(/\s+/)
            .map((p) => p[0])
            .slice(0, 2)
            .join('')
            .toUpperCase()}
        </Avatar>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Space size={8} align="baseline" wrap>
            <Typography.Title level={3} style={{ margin: 0 }}>
              {student.name}
            </Typography.Title>
            <Tag color="processing">
              {student.grade}
              {student.section ? ` · ${student.section}` : ''}
            </Tag>
            <Tag color={student.status === 'enrolled' ? 'success' : student.status === 'graduated' ? 'processing' : 'default'}>
              {student.status}
            </Tag>
          </Space>
          <Typography.Text
            type="secondary"
            style={{ display: 'block', fontSize: 12, fontFamily: "'JetBrains Mono', ui-monospace, monospace", marginTop: 2 }}
          >
            adm. {student.admission_number} · {student.gender} · DOB {student.dob} · added {formatDate(student.created_at)}
          </Typography.Text>
          <div style={{ display: 'flex', gap: 20, marginTop: 10, flexWrap: 'wrap' }}>
            {primary ? (
              <Field
                label="Primary guardian"
                value={
                  <>
                    {primary.name} · {primary.relation}
                    <Typography.Text
                      type="secondary"
                      style={{
                        fontSize: 11.5,
                        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                        marginLeft: 6,
                      }}
                    >
                      {primary.phone}
                    </Typography.Text>
                  </>
                }
              />
            ) : (
              <Field label="Primary guardian" value={<Typography.Text type="secondary">none on file</Typography.Text>} />
            )}
            {secondaries.map((g) => (
              <Field
                key={g.id}
                label="Other guardian"
                value={
                  <>
                    {g.name} · {g.relation}
                    <Typography.Text
                      type="secondary"
                      style={{
                        fontSize: 11.5,
                        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                        marginLeft: 6,
                      }}
                    >
                      {g.phone}
                    </Typography.Text>
                  </>
                }
              />
            ))}
          </div>
        </div>
        <Space size={8}>
          <Button icon={<MessageOutlined />} disabled title="Broadcasts module ships later">
            Message guardian
          </Button>
          <Button icon={<CalendarOutlined />} disabled title="Certificates module ships later">
            Issue certificate
          </Button>
          <Button type="primary" icon={<DollarOutlined />} disabled title="Fee ledger ships with the fees module">
            Open fee ledger
          </Button>
        </Space>
      </div>

      <Tabs
        defaultActiveKey="overview"
        items={[
          { key: 'overview', label: 'Overview', children: <OverviewTab student={student} guardians={guardians} /> },
          { key: 'academics', label: 'Academics', children: <TabWaiting name="Academics" module="exams + subjects" /> },
          { key: 'attendance', label: 'Attendance', children: <TabWaiting name="Attendance" module="daily register" /> },
          { key: 'fees', label: 'Fees', children: <TabWaiting name="Fees" module="fee ledger" /> },
          { key: 'conduct', label: 'Conduct', children: <TabWaiting name="Conduct" module="observations" /> },
          { key: 'documents', label: 'Documents', children: <TabWaiting name="Documents" module="documents + storage" /> },
          { key: 'timeline', label: 'Timeline', children: <TabWaiting name="Timeline" module="cross-module activity feed" /> },
        ]}
      />
    </>
  );
}

function OverviewTab({ student, guardians }: { student: StudentDetail['student']; guardians: StudentGuardianLink[] }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 320px', gap: 12, alignItems: 'flex-start' }}>
      <Card size="small" title="Details">
        <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', rowGap: 8, columnGap: 12 }}>
          <FieldRow label="Admission no." value={student.admission_number} mono />
          <FieldRow label="Name" value={student.name} />
          <FieldRow label="Gender" value={student.gender} />
          <FieldRow label="Date of birth" value={student.dob} mono />
          <FieldRow label="Grade" value={student.grade} />
          <FieldRow label="Section" value={student.section || <Typography.Text type="secondary">—</Typography.Text>} />
          <FieldRow label="Status" value={student.status} />
          <FieldRow label="Added" value={formatDate(student.created_at)} />
        </div>
      </Card>

      <Card size="small" title={`Guardians (${guardians.length})`}>
        {guardians.length === 0 ? (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            No guardians on file.
          </Typography.Text>
        ) : (
          guardians.map((g, i) => (
            <div
              key={g.id}
              style={{
                padding: '10px 0',
                borderTop: i === 0 ? 'none' : '1px solid #f1f1f4',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 500, color: '#14213d', fontSize: 13 }}>{g.name}</span>
                <Tag>{g.relation}</Tag>
                {g.is_primary && <Tag color="success">primary</Tag>}
              </div>
              <div style={{ fontSize: 12, color: '#5b6478', marginTop: 4 }}>
                <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{g.phone}</span>
                {g.email && <span style={{ marginLeft: 10 }}>{g.email}</span>}
              </div>
            </div>
          ))
        )}
      </Card>
    </div>
  );
}

function TabWaiting({ name, module }: { name: string; module: string }) {
  return (
    <Card size="small">
      <Typography.Text type="secondary">
        The <b>{name}</b> tab ships with the {module} module. The student record is real; the derived
        data on this tab isn't tracked yet.
      </Typography.Text>
    </Card>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 10.5, letterSpacing: 0.4, textTransform: 'uppercase', color: '#8a94a6', fontWeight: 600 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, color: '#26304a', marginTop: 2 }}>{value}</div>
    </div>
  );
}

function FieldRow({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <>
      <div style={{ fontSize: 12, color: '#8a94a6' }}>{label}</div>
      <div style={{ fontSize: 13, color: '#14213d', fontFamily: mono ? "'JetBrains Mono', ui-monospace, monospace" : undefined }}>
        {value}
      </div>
    </>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}
